/**
 * native.js — Capacitor native bridge
 *
 * Initialises native plugins when running inside a Capacitor shell.
 * On the web this file is a safe no-op (all calls check Capacitor.isNativePlatform()).
 */
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Browser } from '@capacitor/browser';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { Device } from '@capacitor/device';
import { Share } from '@capacitor/share';
import { Dialog } from '@capacitor/dialog';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { PrivacyScreen } from '@capacitor-community/privacy-screen';
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar';

/* ── Helpers ─────────────────────────────────────────────── */

export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

/* ── Safe-area insets ────────────────────────────────────── */

function applyInsets(insets) {
  if (!insets || typeof document === 'undefined') return;
  const root = document.documentElement.style;
  const px = (v) => `${Math.max(0, Math.round(Number(v) || 0))}px`;
  root.setProperty('--sai-top', px(insets.top));
  root.setProperty('--sai-right', px(insets.right));
  root.setProperty('--sai-bottom', px(insets.bottom));
  root.setProperty('--sai-left', px(insets.left));
}

async function initSafeAreaInsets() {
  if (!isNative()) return;
  const res = await SafeArea.getSafeAreaInsets();
  applyInsets(res?.insets);
  /* Re-read on every native change — rotation, keyboard, notch toggle */
  await SafeArea.addListener('safeAreaChanged', (data) => applyInsets(data?.insets));
}

/* ── Initialise (call once from index.js) ────────────────── */

export async function initNative() {
  if (!isNative()) return;

  /* Status bar — overlay the WebView on both platforms so content can
     render edge-to-edge under the status bar. Safe-area insets (read
     below) push real content out from under it via CSS variables. */
  try {
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setOverlaysWebView({ overlay: true });
    if (getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#00000000' });
    }
  } catch (e) {
    console.warn('[native] StatusBar init failed:', e);
  }

  /* Safe-area insets — read from native window insets and expose as
     CSS variables so every platform behaves like iOS with
     viewport-fit=cover. The plugin fires on every change (rotation,
     keyboard, fold). CSS reads them via var(--sai-*). */
  try {
    await initSafeAreaInsets();
  } catch (e) {
    console.warn('[native] SafeArea init failed:', e);
  }

  /* Privacy screen — blur the app when it's backgrounded (task
     switcher / recents). Expected behaviour for apps that handle
     finance data. On iOS this draws the launch image over the app
     snapshot. On Android this applies FLAG_SECURE, which also blocks
     screenshots. */
  try {
    await PrivacyScreen.enable();
  } catch (e) {
    console.warn('[native] PrivacyScreen enable failed:', e);
  }

  /* Splash screen — hide after React mounts */
  try {
    await SplashScreen.hide();
  } catch (e) {
    console.warn('[native] SplashScreen hide failed:', e);
  }

  /* Keyboard — push body up on iOS to keep inputs visible */
  try {
    if (getPlatform() === 'ios') {
      await Keyboard.setResizeMode({ mode: 'body' });
    }
  } catch (e) {
    console.warn('[native] Keyboard init failed:', e);
  }

  /* Android back button — navigate back or exit */
  try {
    await CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    });
  } catch (e) {
    console.warn('[native] Back button listener failed:', e);
  }

  /* Deep links */
  try {
    await CapApp.addListener('appUrlOpen', (event) => {
      const url = new URL(event.url);
      const path = url.pathname;
      if (path) {
        window.location.hash = path;
      }
    });
  } catch (e) {
    console.warn('[native] Deep link listener failed:', e);
  }
}

/* ── Push Notifications ──────────────────────────────────── */

export async function registerPushNotifications(onToken, onNotification) {
  if (!isNative()) return;

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') {
    console.warn('[native] Push notification permission denied');
    return;
  }

  await PushNotifications.register();

  await PushNotifications.addListener('registration', (token) => {
    console.log('[native] Push token:', token.value);
    if (onToken) onToken(token.value);
  });

  await PushNotifications.addListener('registrationError', (err) => {
    console.error('[native] Push registration error:', err);
  });

  await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[native] Notification received:', notification);
    if (onNotification) onNotification(notification);
  });

  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[native] Notification action:', action);
    if (onNotification) onNotification(action.notification);
  });
}

/* ── Local Notifications (Smart Home alerts) ───────────── */

let _localNotifId = 1;

export async function fireLocalNotification(title, body, data) {
  if (!isNative()) {
    /* Web fallback: use Notification API if permitted */
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification(title, { body, data });
    }
    return;
  }

  try {
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== 'granted') return;

    await LocalNotifications.schedule({
      notifications: [{
        title,
        body,
        id: _localNotifId++,
        schedule: { at: new Date(Date.now() + 100) },
        sound: 'default',
        extra: data || {},
      }],
    });
  } catch (e) {
    console.warn('[native] Local notification error:', e);
  }
}

/* ── Camera ──────────────────────────────────────────────── */

export async function takePhoto() {
  if (!isNative()) return null;
  try {
    const photo = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Prompt, // lets user pick camera or gallery
    });
    return photo.dataUrl;
  } catch (e) {
    // User cancelled or camera unavailable
    console.warn('[native] Camera error:', e);
    return null;
  }
}

/* ── External URLs ───────────────────────────────────────── */

/**
 * Open an external URL. On native Capacitor (iOS/Android) the plain
 * <a target="_blank"> pattern does not navigate because the WebView
 * only loads the bundled app — Capacitor's Browser plugin has to be
 * invoked explicitly. On the web we fall back to window.open.
 *
 * Safe to call from any anchor's onClick handler. Returns true if the
 * URL was handled so callers can preventDefault the original click.
 */
export async function openExternalUrl(url) {
  if (!url) return false;
  try {
    if (isNative()) {
      await Browser.open({ url });
    } else if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    return true;
  } catch (e) {
    console.warn('[native] openExternalUrl failed:', e);
    return false;
  }
}

/* ── Haptics ─────────────────────────────────────────────── */

export async function hapticTap() {
  if (!isNative()) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {}
}

export async function hapticSuccess() {
  if (!isNative()) return;
  try {
    await Haptics.notification({ type: 'SUCCESS' });
  } catch {}
}

/* ── Biometric Auth ──────────────────────────────────────── */

/**
 * Check if biometric hardware is available on this device.
 * Returns { isAvailable, biometryType } or { isAvailable: false }.
 * biometryType: 1 = Touch ID / Fingerprint, 2 = Face ID, 3 = Iris
 */
export async function checkBiometrics() {
  if (!isNative()) return { isAvailable: false };
  try {
    const result = await NativeBiometric.isAvailable();
    return result;
  } catch {
    return { isAvailable: false };
  }
}

/**
 * Prompt the user for biometric verification.
 * Returns true if authenticated, false otherwise.
 */
export async function verifyBiometric(reason) {
  if (!isNative()) return false;
  try {
    await NativeBiometric.verifyIdentity({
      reason: reason || 'Unlock Command',
      title: 'Unlock Command',
      subtitle: 'Verify your identity to continue',
      useFallback: true,        // allow device passcode as fallback
      maxAttempts: 3,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Biometric lock preference helpers — stored in localStorage.
 * The user opts in; the app checks on resume.
 */
const BIOMETRIC_PREF_KEY = 'cmd_biometric_lock';

export function isBiometricLockEnabled() {
  try { return localStorage.getItem(BIOMETRIC_PREF_KEY) === '1'; } catch { return false; }
}

export function setBiometricLockEnabled(enabled) {
  try { localStorage.setItem(BIOMETRIC_PREF_KEY, enabled ? '1' : '0'); } catch {}
}

/* ── Device info ─────────────────────────────────────────── */

/**
 * Returns a lightweight device descriptor for analytics, support
 * tickets, and server-side device binding. On web returns a partial
 * object based on navigator.userAgent.
 * { platform, model, osVersion, manufacturer, webViewVersion, identifier }
 */
export async function getDeviceInfo() {
  try {
    const info = await Device.getInfo();
    const id = await Device.getId();
    return { ...info, identifier: id?.identifier || null };
  } catch {
    return {
      platform: 'web',
      model: navigator.userAgent || 'unknown',
      osVersion: '',
      manufacturer: '',
      webViewVersion: '',
      identifier: null,
    };
  }
}

/* ── Share ───────────────────────────────────────────────── */

/**
 * Open the native share sheet. On web falls back to the Web Share API
 * when available, else copies the URL to clipboard as a last resort.
 * Returns true if the share was presented/handled.
 */
export async function shareContent({ title, text, url, dialogTitle }) {
  try {
    if (isNative()) {
      await Share.share({ title, text, url, dialogTitle });
      return true;
    }
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return true;
    }
    if (url && navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    /* User cancelled share — treated as a non-error. */
  }
  return false;
}

/* ── Dialogs ─────────────────────────────────────────────── */

/**
 * Native-styled alert. On web falls back to window.alert.
 */
export async function showAlert(message, title = '') {
  if (isNative()) {
    try { await Dialog.alert({ title, message }); return; } catch {}
  }
  try { window.alert(message); } catch {}
}

/**
 * Native-styled confirm. Returns true if the user tapped OK.
 */
export async function showConfirm(message, title = '', okText, cancelText) {
  if (isNative()) {
    try {
      const res = await Dialog.confirm({ title, message, okButtonTitle: okText, cancelButtonTitle: cancelText });
      return !!res?.value;
    } catch { return false; }
  }
  try { return window.confirm(message); } catch { return false; }
}

/**
 * Native-styled prompt. Returns the entered string, or null if cancelled.
 */
export async function showPrompt(message, title = '', defaultValue = '') {
  if (isNative()) {
    try {
      const res = await Dialog.prompt({ title, message, inputText: defaultValue });
      return res?.cancelled ? null : (res?.value ?? null);
    } catch { return null; }
  }
  try {
    const v = window.prompt(message, defaultValue);
    return v == null ? null : v;
  } catch { return null; }
}

/* ── Filesystem ─────────────────────────────────────────── */

/**
 * Save a text file (e.g. CSV, JSON export) to the user's Documents
 * directory so it's visible in the platform Files app. Returns the
 * file URI on success, or null on failure / web (web should use
 * a Blob + <a download> instead).
 */
export async function saveTextFile(filename, data, { mime = 'text/plain' } = {}) {
  if (!isNative()) {
    try {
      const blob = new Blob([data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      return url;
    } catch { return null; }
  }
  try {
    const res = await Filesystem.writeFile({
      path: filename,
      data,
      directory: Directory.Documents,
      encoding: Encoding.UTF8,
      recursive: true,
    });
    return res?.uri || null;
  } catch (e) {
    console.warn('[native] saveTextFile failed:', e);
    return null;
  }
}

/* ── Device calendar sync ───────────────────────────────── */

/**
 * Request calendar read/write permission.
 * Returns true if granted. The plugin prompts the user the first time.
 */
export async function requestCalendarPermission(kind = 'readWrite') {
  if (!isNative()) return false;
  try {
    const res = kind === 'read'
      ? await CapacitorCalendar.requestReadOnlyCalendarAccess()
      : await CapacitorCalendar.requestFullCalendarAccess();
    return res?.result === 'granted';
  } catch (e) {
    console.warn('[native] Calendar permission failed:', e);
    return false;
  }
}

/**
 * List calendars available on the device (so the user can pick a
 * default destination in settings).
 */
export async function listDeviceCalendars() {
  if (!isNative()) return [];
  try {
    const { result } = await CapacitorCalendar.listCalendars();
    return result || [];
  } catch (e) {
    console.warn('[native] listCalendars failed:', e);
    return [];
  }
}

/**
 * Create an event on the device calendar. `start` and `end` accept
 * Date objects or millisecond timestamps. Returns the created event
 * ID or null on failure. Requires full-access permission.
 */
export async function createDeviceCalendarEvent({ title, start, end, location, notes, calendarId, isAllDay }) {
  if (!isNative()) return null;
  try {
    const startDate = start instanceof Date ? start.getTime() : Number(start);
    const endDate = end instanceof Date ? end.getTime() : Number(end);
    const { result } = await CapacitorCalendar.createEvent({
      title,
      calendarId,
      location,
      notes,
      startDate,
      endDate,
      isAllDay: !!isAllDay,
    });
    return result || null;
  } catch (e) {
    console.warn('[native] createEvent failed:', e);
    return null;
  }
}

/**
 * List events between two dates (ms timestamps or Date objects).
 */
export async function listDeviceCalendarEvents(from, to) {
  if (!isNative()) return [];
  try {
    const startDate = from instanceof Date ? from.getTime() : Number(from);
    const endDate = to instanceof Date ? to.getTime() : Number(to);
    const { result } = await CapacitorCalendar.listEventsInRange({ startDate, endDate });
    return result || [];
  } catch (e) {
    console.warn('[native] listEvents failed:', e);
    return [];
  }
}

/* ── Re-exports for convenience ──────────────────────────── */
export { Camera, CameraResultType, CameraSource };
export { Directory, Encoding };
